import { useSupabaseClient } from "@supabase/auth-helpers-react";
import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleNewUserSignup } from "../api/accessRequest.api";
import { SignupData } from "../api/auth.types";
import { useToast } from "./ToastContext";

interface SignupAuthContextProps {
  isLoading: boolean;
  signupError: string | null;
  signup: (data: SignupData) => Promise<void>;
  signUpWithGoogle: () => Promise<void>;
}

const SignupAuthContext = createContext<SignupAuthContextProps | undefined>(
  undefined
);

export function useSignupAuth() {
  const context = useContext(SignupAuthContext);
  if (context === undefined) {
    throw new Error("useSignupAuth must be used within a SignupAuthProvider");
  }
  return context;
}

export function SignupAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const supabase = useSupabaseClient();
  const [isLoading, setIsLoading] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const { showToast } = useToast();

  async function signup(data: SignupData) {
    setIsLoading(true);
    setSignupError(null);

    try {
      const existingUserId = data?.existingUserId;
      if (!data.email) {
        const message = "Email is required to sign up";
        setSignupError(message);
        showToast(message, "error");
        return;
      }

      const { data: signUpData, error } = await supabase.auth.signUp({
        email: data.email as string,
        password: data.password as string,
        options: {
          data: {
            email: data.email as string,
            first_name: data.firstName,
            last_name: data.lastName,
            phone: data.phone,
            existing_user_id: existingUserId,
          },
        },
      });

      if (error) {
        setSignupError(error.message);
        showToast(error.message, "error");
      } else if (signUpData.user) {
        if (data.slug) {
          await handleNewUserSignup({ slug: data.slug });
        }
        showToast("Account created", "success");
        navigate("/download-app", { replace: true });
      }
    } catch (error: any) {
      setSignupError(error.message || "An error occurred during sign up");
      showToast(error.message || "An error occurred during sign up", "error");
    } finally {
      setIsLoading(false);
    }
  }

  async function signUpWithGoogle() {
    setIsLoading(true);
    setSignupError(null);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.href,
        },
      });

      if (error) {
        setSignupError(error.message);
        showToast(error.message, "error");
      }
    } catch (error: any) {
      setSignupError(
        error.message || "An error occurred during Google sign in"
      );
      showToast(
        error.message || "An error occurred during Google sign in",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  }

  const value = {
    isLoading,
    signupError,
    signup,
    signUpWithGoogle,
  };

  return (
    <SignupAuthContext.Provider value={value}>
      {children}
    </SignupAuthContext.Provider>
  );
}

